import { selector } from 'lib/selectors';
import { userGUIDSelector } from 'domain/env/envSelector';
import { documentApprovalStampPositionSelector } from 'domain/documents/selectors/getDocumentApprovalStampPositionSelector';

import { UserEnhancedApprovalFactory } from 'domain/approvals/helpers';
import { checkEditActionInvisibility } from 'pages/company/WorkspaceRoute/components/DocumentContextMenu/helpers';

// this can not be imported from reconciliation
export const approvals = (state) => state.approvals;
export const approvalsListSelector = selector(approvals, (a) => a.list);
export const approvalsLevelsListSelector = selector(approvals, (a) => a.levelsList);
export const approvalsAuthorSelector = selector(approvals, (a) => a.author);
export const approvalsEditableSelector = selector(approvals, (a) => a.editable);
export const approvalsSignatureSelector = selector(approvals, (a) => a.signature);
export const approvalsCurrentSignersSelector = selector(approvals, (a) => a.currentSigners);
export const approvalsStatusSelector = selector(approvals, (a) => a.status);
export const approvalsStatusAsBoolSelector = selector(approvals, (a) => a.status !== 'draft' && a.status !== null);
export const isCurrentUserSignaturePendingSelector = selector(
  approvalsCurrentSignersSelector,
  userGUIDSelector,
  approvalsStatusAsBoolSelector,
  (signersPending, currentUserGuid, isFlowActive) => signersPending.includes(currentUserGuid) && isFlowActive,
);
export const isSignApprovalsStartSelector = selector(
  approvalsListSelector,
  approvalsLevelsListSelector,
  (approvalList, approvalLevels) => {
    const firstLevel = approvalLevels.first();
    const first = approvalList.first();

    const firstApproved = first ? first.get('status') === 'approved' : false;
    const firstApprovedOnLevel = firstLevel ? firstLevel.approvers.some((a) => a.status === 'approved') : false;

    return firstApproved || firstApprovedOnLevel;
  },
);

export const approversSelector = selector(approvals, (approval) => approval.approvers);

export const userEnhancedApprovalsListSelector = selector(
  approvalsListSelector,
  approversSelector,
  (approvalList, approvers) =>
    approvalList.map((approval) => {
      const approver = approvers.find((u) => u.username === approval.email);
      return UserEnhancedApprovalFactory({
        userGuid: approval.userGuid,
        tag: approval.email,
        id: approval.id,
        picture: approval.picture,
        status: approval.status,
        fullName: approver?.fullName || approval.email,
      });
    }),
);

export const approvalsRejectReasonsSelector = selector(approvals, (approval) => approval.rejectReasonsList);

export const approvalsRejectReasonIdSelector = selector(approvals, (approval) => approval.rejectReason);

export const approvalsRejectReasonDetailsSelector = selector(approvals, (approval) => approval.rejectMessage);

export const approvalsRejectReasonTextSelector = selector(
  approvalsRejectReasonIdSelector,
  approvalsRejectReasonsSelector,
  (id, list) => (id ? list.get(id, null) : null),
);

export const approveFormFieldsListSelector = selector(approvals, (approval) => approval.approveFormFieldsList);

export const getApprovalStampModeByApprovals = selector(
  documentApprovalStampPositionSelector,
  approvalsListSelector,
  approvalsStatusSelector,
  approvalsLevelsListSelector,
  (approvalStampPosition, list, status, approvalLevels) => {
    // If the AF status is not active and there is no stamp on the document and list of approver are greater than zero -
    // we can edit the position of the stamp
    if (
      approvalStampPosition.toSeq().every((value) => value === null) &&
      (list.size > 0 || approvalLevels.size > 0) &&
      status !== 'active'
    ) {
      return 'edit';
    }

    return 'hidden';
  },
);

export const approvalGroupListSelector = selector(approvals, (approval) => approval.approvalGroups);

export const approvalGroupsLoadedSelector = selector(approvals, (approval) => approval.approvalGroupsLoaded);

export const allApprovalUsersNotApproversSelector = selector(approvals, (approval) =>
  approval.allApprovalUsers.filter((u) => !u.isApprover),
);

export const allApprovalUsersLoadedSelector = selector(approvals, (approval) => approval.allApprovalUsersLoaded);

const approvalActiveGroupIdSelector = selector(approvals, (approval) => approval.groupId);

export const approvalActiveAdvancedGroupIdSelector = selector(approvals, (approval) => approval.advancedGroupId);

export const activeGroupNameEnhancedSelector = selector(
  approvalActiveGroupIdSelector,
  approvalGroupListSelector,
  (activeGroupId, groupsList) => {
    const groupName = groupsList.find((g) => g.group_id === activeGroupId);
    return groupName ? groupName.group_title : null;
  },
);

export const approversLoadedSelector = selector(approvals, (approval) => approval.approversLoaded);

export const advancedApprovalGroupListSelector = selector(approvals, (approval) => approval.advancedApprovalGroups);

export const advancedApprovalGroupsLoadedSelector = selector(
  approvals,
  (approval) => approval.advancedApprovalGroupsLoaded,
);

export const activeAdvancedGroupNameEnhancedSelector = selector(
  approvalActiveAdvancedGroupIdSelector,
  advancedApprovalGroupListSelector,
  (activeAdvancedGroupId, advancedGroupsList) => {
    const groupName = advancedGroupsList.find((g) => g.id === activeAdvancedGroupId);
    return groupName ? groupName.name : null;
  },
);

export const userEnhancedApprovalsLevelsListSelector = selector(
  approvalsLevelsListSelector,
  approversSelector,
  (approvalLevels, approvers) =>
    approvalLevels
      .map(({ approvers: approversNodes, minAmount, minApprovers, flowType, status }) => ({
        minAmount,
        minApprovers,
        flowType,
        status,
        approvers: approversNodes.map((approval) => {
          const approver = approvers.find((u) => u.username === approval.username);
          return UserEnhancedApprovalFactory({
            userGuid: approval.username,
            tag: approval.username,
            id: approval.userid,
            picture: approver?.picture,
            status: approval.status,
            fullName: approver?.fullName || approval.username,
          });
        }),
      }))
      .toJS(),
);

export const checkApprovalsForDocumentEditActionInvisibilitySelector = selector(
  approvalsStatusSelector,
  approvalsListSelector,
  approvalsLevelsListSelector,
  (status, list, levelList) => {
    return checkEditActionInvisibility(status, list?.toArray(), levelList?.toArray());
  },
);

export const isVendorBasedApprovalsDefaultsLoadingSelector = selector(
  approvals,
  (approval) => approval.vendorBasedApprovalsDefaultsLoading,
);

export const vendorBasedApprovalsDefaultsSelector = selector(
  approvals,
  (approval) => approval.vendorBasedApprovalsDefaults,
);
